import React from 'react';
import type { IntlShape } from 'react-intl';
import { FormattedMessage } from 'react-intl';
import { styled } from '@compiled/react';

import type { AnnotationUpdateEmitter } from '@atlaskit/editor-common/annotation';
import { AnnotationUpdateEvent } from '@atlaskit/editor-common/types';
import Button from '@atlaskit/button/custom-theme-button';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { default as CommentIconSVG } from '@atlaskit/icon/core/comment';
import type { TriggerProps } from '@atlaskit/popup';

import { getRendererAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getRendererAnnotationEventEmitter.ts';
import { getEditorAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getEditorAnnotationEventEmitter.ts';
import { GeneralShortcutListener } from '@confluence/shortcuts/entry-points/GeneralShortcutListener.ts';
import { useShortcutStrings } from '@confluence/shortcuts/entry-points/ShortcutVisualizer.ts';
import { ShortcutListener } from '@confluence/shortcuts/entry-points/ShortcutListener.ts';
import {
	TOGGLE_INLINE_COMMENTS_SHORTCUT_RENDERER,
	UNIVERSAL_TOGGLE_INLINE_COMMENTS_SHORTCUT,
} from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';
import {
	useAnnotations,
	useAnnotationsDispatch,
} from '@confluence/annotation-provider-store/entry-points/AnnotationsContext.ts';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';
import {
	useCommentsContentState,
	useCommentsContentActions,
} from '@confluence/comment-context/entry-points/useCommentsContent.ts';
import { CommentWarningDialog } from '@confluence/comment-dialogs';
import { fg } from '@confluence/feature-gating';
import { useLivePageMode } from '@confluence/live-pages-utils/entry-points/useLivePagesStore.ts';

import {
	toggleCommentEditor,
	toggleCommentEditorWithAnnotationManagers,
} from './CommentButtonUtils.tsx';

export type PageModeType = 'view' | 'edit';

type CommentIconProps = {
	triggerProps?: TriggerProps;
	intl: IntlShape;
	handleCommentButtonClick: () => void;
	commentButtonRef?: React.MutableRefObject<HTMLDivElement | null>;
	isDisabled?: boolean;
	contentId?: string;
	pageMode?: PageModeType;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CommentButtonWrapper = styled.div({
	display: 'flex',
	flexDirection: 'row',
});

const CommentTooltipText = () => {
	return (
		<FormattedMessage
			id="comment-button.tooltip"
			defaultMessage="Show comments"
			description="Tooltip for button that shows inline comments for a page"
		/>
	);
};

export const CommentIcon = ({
	triggerProps,
	intl,
	handleCommentButtonClick,
	commentButtonRef,
	isDisabled = false,
	pageMode = 'view',
}: CommentIconProps) => {
	const eventEmitter: AnnotationUpdateEmitter = getRendererAnnotationEventEmitter();
	const editorEmitter = getEditorAnnotationEventEmitter();
	const { editorViewProps } = useAnnotations();
	const { setEditorViewProps } = useAnnotationsDispatch();
	const { showModal } = useDialogs();
	const { hasContentChanged } = useCommentsContentState();
	const { resetContentChanged } = useCommentsContentActions();
	const [{ isViewMode: isLiveViewMode }] = useLivePageMode();

	const commentShortcutStrings = useShortcutStrings({
		shortcut: UNIVERSAL_TOGGLE_INLINE_COMMENTS_SHORTCUT,
		isEditorShortcut: pageMode === 'edit' && !isLiveViewMode,
	});

	return (
		<CommentButtonWrapper data-testid="comment-icon" ref={commentButtonRef} data-vc="comment-icon">
			{/* View shortcut should apply for view page or live pages view mode */}
			{(pageMode === 'view' || isLiveViewMode) && (
				<GeneralShortcutListener
					deprecatedAccelerator={TOGGLE_INLINE_COMMENTS_SHORTCUT_RENDERER}
					accelerator={UNIVERSAL_TOGGLE_INLINE_COMMENTS_SHORTCUT}
					listener={() => {
						if (document.getElementById('renderer-comments-sidebar')) {
							eventEmitter.emit(AnnotationUpdateEvent.REMOVE_ANNOTATION_FOCUS);
							eventEmitter.emit(AnnotationUpdateEvent.DESELECT_ANNOTATIONS);
						} else {
							handleCommentButtonClick();
						}
					}}
				/>
			)}
			{/* Edit shortcut should apply for edit pages but not live view mode */}
			{pageMode === 'edit' && !isLiveViewMode && (
				<ShortcutListener
					accelerator={UNIVERSAL_TOGGLE_INLINE_COMMENTS_SHORTCUT}
					listener={() => {
						// This has to live here so it can be globally listened to, if we put it inside of
						// the sidebar, then it will only trigger when the sidebar has focus
						if (fg('confluence_frontend_editor_annotation_manager')) {
							// If the content has changed in view, ask the user if they want to discard
							if (editorViewProps && hasContentChanged) {
								showModal(CommentWarningDialog, {
									onConfirm: () => {
										resetContentChanged();
										toggleCommentEditorWithAnnotationManagers(
											editorEmitter,
											handleCommentButtonClick,
											editorViewProps,
											setEditorViewProps,
										);
									},
								});
							} else {
								toggleCommentEditorWithAnnotationManagers(
									editorEmitter,
									handleCommentButtonClick,
									editorViewProps,
									setEditorViewProps,
								);
							}
						} else {
							toggleCommentEditor(editorEmitter, handleCommentButtonClick);
						}
					}}
				/>
			)}
			<Tooltip
				content={<CommentTooltipText />}
				shortcut={commentShortcutStrings}
				hideTooltipOnClick
				position="bottom"
			>
				<Button
					testId="comment-button"
					appearance="subtle"
					iconBefore={
						<CommentIconSVG
							spacing="spacious"
							label={intl.formatMessage({
								id: 'comment-button.icon.label',
								description:
									'aria-label for the comment button to display the first inline comment',
								defaultMessage: 'Show comments',
							})}
						/>
					}
					isDisabled={isDisabled}
					onClick={handleCommentButtonClick}
					{...(triggerProps || {})}
				/>
			</Tooltip>
		</CommentButtonWrapper>
	);
};
